import React from 'react';
import { Component, Container, HBox, Position, PuzzleButton, VBox } from "puzzle";
import {
  Chart, ChartLegend,
  ChartSeries,
  ChartSeriesItem, ChartTooltip, SeriesClickEvent,
} from "@progress/kendo-react-charts";
import * as _ from "lodash";
import 'hammerjs'; //TODO Remove once we migrate to newer kendo - necessary for legend left click
import { STATUSES } from "../../Types";
import GlobalInfoProvider from "../../../util/GlobalInfo";

const reversedStatusMapper = {
  "COMPLETED_SUBMISSION_STATUS": "chartSegmentCompletedFilter",
  "DELIVERED_SUBMISSION_STATUS": "chartSegmentDeliveredFilter",
  "OVERDUE": "chartSegmentOverdueFilter",
  "DUESOON": "chartSegmentDueSoonFilter",
  "ACTIVE_SUBMISSION_STATUS": "chartSegmentActiveFilter",
  "ON_HOLD_SUBMISSION_STATUS": "chartSegmentOnHoldFilter"
}

type STATISTIC = {
  color: string
  count: number
  visible: boolean
  inner: boolean
  status: STATUSES
}

type STATE = {
  innerData: STATISTIC[],
  outerData: STATISTIC[],
  submissionCount: number,
  screenWidth: number
}

const colorMapper: Record<string, string> = {
  "#0073ea": "#1d8cff",
  "#ffc634": "#ffd260",
  "#48cf59": "#78e585",
  "#008a12": "#20ae33",
  "#ff650f": "#FF9c65",
  "#ee0303": "#ff5959"
}

const postStatusMapper: Record<string, string[]> = {
  "ACTIVE_SUBMISSION_STATUS": ["FAILED", "IN_PROCESS", "READY"],
  "COMPLETED_SUBMISSION_STATUS": ["PROCESSED"],
  "DELIVERED_SUBMISSION_STATUS": ["DELIVERED"],
  "ON_HOLD_SUBMISSION_STATUS": ["WAITING", "PREFLIGHT"],
}

const initInnerData: STATISTIC[] = [
  { status: "OVERDUE", count: 0, color: "#ee0303", visible: false, inner: true },
  { status: "DUESOON", count: 0, color: "#ff650f", visible: false, inner: true }
];

const initOuterData: STATISTIC[] = [
  { status: "ACTIVE_SUBMISSION_STATUS", count: 0, color: "#0073ea", visible: false, inner: false },
  { status: "ON_HOLD_SUBMISSION_STATUS", count: 0, color: "#ffc634", visible: false, inner: false },
  { status: "COMPLETED_SUBMISSION_STATUS", count: 0, color: "#48cf59", visible: false, inner: false },
  { status: "DELIVERED_SUBMISSION_STATUS", count: 0, color: "#008a12", visible: false, inner: false }
];

class Statistics extends Component <{}, STATE> {
  state: STATE = {
    innerData: [],
    outerData: [],
    submissionCount: 0,
    screenWidth: 0
  }

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    window.addEventListener('resize', this.handleResize);
    this.setState({
      screenWidth: window.innerWidth
    })
  }

  /**
   * Clean up the listener when the component unmounts
   */
  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  /**
   * Handler for window resize
   */
  handleResize =  _.debounce(() => {
    this.setState({
      screenWidth: window.innerWidth
    });
  }, 100);

  /**
   * Handle legend click
   *
   * @param item
   */
  handleLegendClick = (item: STATISTIC) => {
    const { status, inner } = item;
    const { innerData, outerData } = this.state;
    let newInnerData = innerData;
    let newOuterData = outerData;
    if (inner) {
      newInnerData = _.cloneDeep(innerData).map((data: STATISTIC) => {
        if (data.status === status) {
          data.visible = !data.visible;
        }
        return data;
      });

    } else {
      newOuterData = _.cloneDeep(outerData).map((data: STATISTIC) => {
        if (data.status === status) {
          data.visible = !data.visible;
        }
        return data;
      })
    }
    const visibleOuterData = newOuterData.filter((data: STATISTIC) => data.visible).length;// prevent unselect the last status
    if (visibleOuterData) {
      if (item.visible) {
        this.setState({
          innerData: newInnerData, outerData: newOuterData
        }, () => this.dispatch('updateFolderViewData', Position.end, {
          dataIndex: reversedStatusMapper[item.status],
          hidden: item.visible //it's necessary to change visibility
        }))
      } else {
        this.updateChartOnLegendClick(newInnerData, newOuterData, {
          ...item,
          visible: !item.visible
        });
      }
    }
  };

  /**
   * Navigate to submission list on chart click
   *
   * @param event
   */
  handleSeriesClick = (event: SeriesClickEvent) => {
    const { status } = event.point.dataItem;
    let filter: any = {};
    if (["OVERDUE", "DUESOON"].includes(status)) {
      filter.pastDueDateComboBox = status;
    } else {
      filter.submissionStatusValues = postStatusMapper[status];
    }
    const operationData = {
      decodeFilter: true,
      filter: { searchData: JSON.stringify({ ...filter }) },
      path: '/view/submissionList'
    };

    this.dispatch('openPrefilteredSubmissionList', '', {
      ...operationData
    });
  };

  /**
   * Statistic skeleton renderer
   */
  renderSkeleton = () => {
    return (
      <HBox className={'pd-chart-section'} flex1 gap={6}>
        <Container className={'tp-loader'}>
          <div className={'tp-loader-rings k-chart'}>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </Container>
        <ul className={'pd-chart-legend'}>
          {initOuterData.concat(initInnerData).map((item: STATISTIC) => (
            <li
              key={item.status}
              className={'tp-font-size tp-text-medium pd-active-status'}
            >
              <span className={'pd-chart-legend-indicator'} style={{ background: item.color }} />
              <HBox justifyContentBetween flex1>
                <span className={'pd-chart-legend-label'}>{this.translate(item.status)}</span>
                <i aria-hidden={true} className={'tp-icon tp-loop tp-icon-sm pd-chart-legend-number'} />
              </HBox>
            </li>
          ))}
        </ul>
      </HBox>
    )
  }

  /**
   * Sets new data for chart
   *
   * @param response
   * @param submissionCountResponse
   */
  setChartData = ({ model }: { model: { submissionsPieChartData: Record<string, number> } }, { submissionCount }: {
    submissionCount: number
  }) => {
    const { submissionsPieChartData } = model;
    let innerData = this.mapChartData(_.cloneDeep(initInnerData), submissionsPieChartData);
    let outerData = this.mapChartData(_.cloneDeep(initOuterData), submissionsPieChartData);
    this.setState({ innerData, outerData, submissionCount })
  };

  /**
   * Maps init data
   *
   * @param dataArray
   * @param submissionsPieChartData
   */
  mapChartData = (dataArray: STATISTIC[], submissionsPieChartData: Record<string, number>) => {
    const isLightTheme = GlobalInfoProvider.getThemeType();
    return dataArray.map(item => {
      item.color = isLightTheme ? item.color : colorMapper[item.color];
      if (submissionsPieChartData.hasOwnProperty(item.status)) {
        return {
          ...item,
          count: submissionsPieChartData[item.status],
          visible: true
        };
      } else {
        return {
          ...item,
          visible: false
        }
      }
    });
  };

  setEmptyChartData = ({ submissionCount }: {
    submissionCount: number
  }) => {
    this.setState({
      innerData: initInnerData,
      outerData: initOuterData,
      submissionCount
    })
  };

  /**
   * Calls handleParentChange with 500ms delay
   *
   * @param innerData
   * @param outerData
   * @param item
   */
  updateChartOnLegendClick = (innerData: STATISTIC[], outerData: STATISTIC[], item: STATISTIC) => {
    const requestedSubmissionsPieData = innerData.concat(outerData).reduce((acc: string[], item: STATISTIC) => {
      if (item.visible) {
        acc.push(item.status)
      }
      return acc;
    }, []);
    this.dispatch('getChartData', {
      dispatcherId: 'chartDispatcher',
      pos: Position.end
    }, {
      configurationToUpdate: {
        dataIndex: reversedStatusMapper[item.status],
        hidden: !item.visible
      },
      payload: {
        folder: 'DEFAULT_SUBMISSION',
        requestedSubmissionsPieData
      },
      chartUrl: '/submissionsPieChart'
    })
  };

  /**
   * View all submission
   */
  viewAllSubmissions = () => {
    const operationData = {
      decodeFilter: true,
      filter: {},
      path: '/view/submissionList'
    };

    this.dispatch('openPrefilteredSubmissionList', '', {
      ...operationData
    });
  };

  renderBody() {
    let { innerData, outerData, submissionCount, screenWidth } = this.state;
    const lowerResolution = screenWidth <= 1520;
    const isLightMode = GlobalInfoProvider.getThemeType();
    return (
      <VBox flex1>
        {
          !(innerData.length || outerData.length) ? this.renderSkeleton() :
            <HBox className={'pd-chart-section'} flex1 gap={6}>
              <Chart transitions={false} onSeriesClick={this.handleSeriesClick}>
                <ChartLegend
                  visible={false}
                />
                <ChartTooltip render={({ point }: any) => {
                  return <div className={'pd-chart-tooltip'}>
                    {this.translate(point?.category)}: {point?.value}
                  </div>
                }} />
                <ChartSeries>
                  <ChartSeriesItem
                    border={{
                      color: isLightMode ? '#fff' : "#202632",
                      dashType: 'solid',
                      width: 1
                    }}
                    type="donut"
                    data={innerData}
                    startAngle={55}
                    categoryField="status"
                    field="count"
                    holeSize={lowerResolution ? 22 : 36}
                    size={lowerResolution ? 10 : 16}
                    margin={lowerResolution ? 4 : 6}
                  />
                  <ChartSeriesItem
                    border={{
                      color: isLightMode ? '#fff' : "#202632",
                      dashType: 'solid',
                      width: 1
                    }}
                    type="donut"
                    data={outerData}
                    categoryField="status"
                    holeSize={lowerResolution ? 36 : 58}
                    size={lowerResolution ? 16 : 25}
                    field="count"
                  />
                </ChartSeries>
              </Chart>
              <ul className={'pd-chart-legend'}>
                {outerData.concat(innerData).map((item: STATISTIC) => (
                  <li
                    key={item.status}
                    className={`tp-font-size tp-text-medium k-cursor-pointer ${item.visible ? 'pd-active-status' : ''}`}
                    onClick={() => this.handleLegendClick(item)}
                  >
                    <span className={'pd-chart-legend-indicator'} style={{ background: item.color }} />
                    <HBox justifyContentBetween flex1>
                      <span className={'pd-chart-legend-label'}>{this.translate(item.status)}</span>{item.visible ?
                      <span className={'pd-chart-legend-number'}>{item.count}</span> : null}
                    </HBox>
                  </li>
                ))}
              </ul>
            </HBox>
        }
        <HBox
          alignItemsCenter
          className={'pd-total-submission-section k-cursor-pointer'}
          flex1
          justifyContentCenter
          onClick={this.viewAllSubmissions}
        >
          <VBox alignItemsCenter className={'tp-100'}>
            <span className={'pd-total-submission-count tp-text-semibold'}>{submissionCount}</span>
            <span className={'tp-font-size-md tp-text-medium'}>{this.translate('totalSubmissions')}</span>
          </VBox>
          <HBox alignItemsEnd justifyContentEnd>
            <PuzzleButton
              titleText={this.translate('viewAllSubmissions')}
              onClick={this.viewAllSubmissions}
              aria-label={this.translate('viewAllSubmissions')}
              iconClass={'tp-icon tp-arrow-chevron-right'}
              fillMode={'flat'}
              extraSmall
              tooltipClassName={'k-align-self-end'}
              withTooltip
            />
          </HBox>
        </HBox>
      </VBox>
    );
  }
}

export default React.memo(Statistics);
